import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { getUserThemeSetting, upsertUserThemeSetting, type UserTheme } from '../services/userSettingsService';

type Theme = UserTheme;
export type UIDensity = 'comfortable' | 'normal' | 'compact';

const THEME_STORAGE_KEY = 'lean-workflow-theme';
const THEME_SNAPSHOT_KEY = 'workflow_theme_snapshot';
const THEME_SCOPE_PREFIX = 'workflow_theme_scope';
const UI_DENSITY_KEY = 'workflow_ui_density';

const LIGHT_META_THEME = '#F4F6F8';
const DARK_META_THEME = '#0B0F14';

const normalizeTheme = (value: string | null | undefined): Theme | null => {
  if (value === 'light' || value === 'midnight' || value === 'black') return value;
  return null;
};

const normalizeDensity = (value: string | null): UIDensity => {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'compact' || normalized === 'compacta') return 'compact';
  if (normalized === 'normal') return 'normal';
  if (normalized === 'comfortable' || normalized === 'confortavel') return 'comfortable';

  return 'compact';
};

const getThemeScopeKey = (userId?: string | null, organizationId?: string | null) =>
  userId && organizationId ? `${THEME_SCOPE_PREFIX}:${userId}:${organizationId}` : null;

const readStoredTheme = (key: string | null): Theme | null => {
  if (!key || typeof window === 'undefined') return null;

  try {
    return normalizeTheme(window.localStorage.getItem(key));
  } catch {
    return null;
  }
};

const readInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'midnight';

  try {
    return (
      normalizeTheme(window.localStorage.getItem(THEME_SNAPSHOT_KEY))
      ?? normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY))
      ?? 'midnight'
    );
  } catch {
    return 'midnight';
  }
};

const persistThemeLocally = (theme: Theme, userId?: string | null, organizationId?: string | null) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    window.localStorage.setItem(THEME_SNAPSHOT_KEY, theme);

    const scopedKey = getThemeScopeKey(userId, organizationId);
    if (scopedKey) {
      window.localStorage.setItem(scopedKey, theme);
    }
  } catch {
    /* ignore */
  }
};

const applyThemeToDocument = (theme: Theme) => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const body = document.body;
  // Both 'midnight' and 'black' activate Tailwind dark: prefix
  root.classList.toggle('dark', theme === 'midnight' || theme === 'black');
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme === 'light' ? 'light' : 'dark';
  body.classList.remove('theme-light', 'theme-midnight', 'theme-black');
  body.classList.add(theme === 'midnight' ? 'theme-midnight' : theme === 'black' ? 'theme-black' : 'theme-light');
  body.style.colorScheme = theme === 'light' ? 'light' : 'dark';

  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor instanceof HTMLMetaElement) {
    metaThemeColor.content = theme === 'light' ? LIGHT_META_THEME : DARK_META_THEME;
  }
};

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  density: UIDensity;
  setDensity: (density: UIDensity) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const [theme, setThemeState] = useState<Theme>(() => readInitialTheme());
  const [density, setDensityState] = useState<UIDensity>(() => {
    if (typeof window === 'undefined') return 'normal';
    return normalizeDensity(window.localStorage.getItem(UI_DENSITY_KEY));
  });

  const activeUserId = user?.id ?? null;
  const activeOrganizationId = workspace?.organizationId ?? user?.organizationId ?? user?.organization_id ?? null;
  const activeScopeKey = useMemo(
    () => getThemeScopeKey(activeUserId, activeOrganizationId),
    [activeOrganizationId, activeUserId],
  );

  useEffect(() => {
    applyThemeToDocument(theme);
    persistThemeLocally(theme, activeUserId, activeOrganizationId);
  }, [activeOrganizationId, activeUserId, theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
    localStorage.setItem(UI_DENSITY_KEY, density);
  }, [density]);

  useEffect(() => {
    let cancelled = false;

    const cachedScopedTheme = readStoredTheme(activeScopeKey);
    if (cachedScopedTheme) {
      setThemeState((current) => (current === cachedScopedTheme ? current : cachedScopedTheme));
    }

    if (!activeUserId || !activeOrganizationId) {
      const fallbackTheme = cachedScopedTheme ?? readInitialTheme();
      setThemeState((current) => (current === fallbackTheme ? current : fallbackTheme));
      return () => {
        cancelled = true;
      };
    }

    const hydrateRemoteTheme = async () => {
      try {
        const remoteTheme = await getUserThemeSetting(activeUserId, activeOrganizationId);
        if (cancelled) return;

        // 'black' is stored locally only — never let remote 'midnight' override a local 'black' preference
        const localPreference = cachedScopedTheme ?? readInitialTheme();
        const nextTheme = localPreference === 'black' ? 'black' : remoteTheme ?? localPreference;
        setThemeState((current) => (current === nextTheme ? current : nextTheme));
        persistThemeLocally(nextTheme, activeUserId, activeOrganizationId);
      } catch (error) {
        if (cancelled) return;

        const fallbackTheme = cachedScopedTheme ?? readInitialTheme();
        setThemeState((current) => (current === fallbackTheme ? current : fallbackTheme));
        console.warn('[ThemeContext] Falha ao carregar user_settings.theme:', error);
      }
    };

    void hydrateRemoteTheme();

    return () => {
      cancelled = true;
    };
  }, [activeOrganizationId, activeScopeKey, activeUserId]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === UI_DENSITY_KEY) {
        setDensityState(normalizeDensity(event.newValue));
        return;
      }

      if (
        event.key === THEME_STORAGE_KEY
        || event.key === THEME_SNAPSHOT_KEY
        || event.key === activeScopeKey
      ) {
        const nextTheme = normalizeTheme(event.newValue);
        if (nextTheme) {
          setThemeState((current) => (current === nextTheme ? current : nextTheme));
        }
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [activeScopeKey]);

  const persistTheme = useCallback(async (nextTheme: Theme) => {
    persistThemeLocally(nextTheme, activeUserId, activeOrganizationId);
    setThemeState(nextTheme);

    if (!activeUserId || !activeOrganizationId) return;

    try {
      await upsertUserThemeSetting({
        userId: activeUserId,
        organizationId: activeOrganizationId,
        theme: nextTheme,
      });
    } catch (error) {
      console.warn('[ThemeContext] Falha ao persistir user_settings.theme:', error);
    }
  }, [activeOrganizationId, activeUserId]);

  const toggleTheme = useCallback(() => {
    const nextTheme: Theme = theme === 'light' ? 'midnight' : theme === 'midnight' ? 'black' : 'light';
    void persistTheme(nextTheme);
  }, [persistTheme, theme]);

  const setTheme = useCallback((nextTheme: Theme) => {
    void persistTheme(nextTheme);
  }, [persistTheme]);

  const setDensity = useCallback((nextDensity: UIDensity) => {
    setDensityState(nextDensity);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, density, setDensity, isDark: theme === 'midnight' || theme === 'black' }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
